import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FirebaseUpload } from '@elitetrainr/capacitor-firebase-upload';
import { Auth, authState, User } from '@angular/fire/auth';
import { FunctionsService } from './functions.service';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

/** Callable name must match the backend (Firebase Functions). */
const SYNC_NATIVE_AUTH_CALLABLE = 'syncNativeFirebaseAuth';

interface SyncNativeAuthResponse {
  customToken: string;
}

/**
 * Minimum time between successful native custom-token syncs for the same Firebase uid.
 * Native FirebaseAuth keeps the session alive via refresh tokens; re-minting a custom token
 * is only needed occasionally (new install, account switch, rare native session loss).
 * This greatly reduces callable volume vs firing on every authState emission / resume.
 */
const NATIVE_SYNC_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

@Injectable({
  providedIn: 'root',
})
export class NativeFirebaseAuthBridgeService {
  private lastSuccessfulNativeSyncAt = 0;
  private lastSyncedUid: string | null = null;
  private nativeSyncInFlight: Promise<boolean> | null = null;

  constructor(
    private functionsService: FunctionsService,
    private firebaseAuth: Auth,
  ) {
    this.subscribeWebAuthToNativeSync();
  }

  /**
   * Whenever the WebView has a Firebase user (sign-in, persistence restore, account change), keep native Auth in sync.
   * `debounceTime` collapses bursts (e.g. guard + initial auth emission).
   * Throttled by NATIVE_SYNC_MIN_INTERVAL_MS for the same uid to limit callable traffic.
   */
  private subscribeWebAuthToNativeSync(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    authState(this.firebaseAuth)
      .pipe(
        distinctUntilChanged((a, b) => (a?.uid ?? '') === (b?.uid ?? '')),
        filter((u): u is User => !!u),
        debounceTime(400),
      )
      .subscribe(() => {
        void this.syncNativeSessionAfterWebAuth();
      });
  }

  /**
   * Mint a custom token (callable) and sign in the native Firebase Auth SDK.
   * Returns true only if native sign-in completed successfully.
   */
  private async runSyncNativeSession(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }
    const uid = this.firebaseAuth.currentUser?.uid;
    if (!uid) {
      return false;
    }
    try {
      const data = (await this.functionsService.callFunction(SYNC_NATIVE_AUTH_CALLABLE, {})) as SyncNativeAuthResponse;
      const token = data?.customToken;
      if (!token || typeof token !== 'string') {
        console.error('[NativeFirebaseAuthBridge] Callable returned no customToken', data);
        return false;
      }
      await FirebaseUpload.signInWithCustomToken({ customToken: token });
      this.lastSuccessfulNativeSyncAt = Date.now();
      this.lastSyncedUid = this.firebaseAuth.currentUser?.uid ?? uid;
      return true;
    } catch (e) {
      console.error('[NativeFirebaseAuthBridge] runSyncNativeSession failed', e);
      return false;
    }
  }

  /**
   * Shared entry: throttle by uid + time, single-flight concurrent syncs.
   * @param force Skip throttle (e.g. upload failed with "no native user").
   */
  private async maybeSyncNativeSession(options?: { force?: boolean }): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const force = options?.force === true;
    const uid = this.firebaseAuth.currentUser?.uid;
    if (!uid) {
      return;
    }

    if (
      !force &&
      uid === this.lastSyncedUid &&
      this.lastSuccessfulNativeSyncAt > 0 &&
      Date.now() - this.lastSuccessfulNativeSyncAt < NATIVE_SYNC_MIN_INTERVAL_MS
    ) {
      return;
    }

    while (this.nativeSyncInFlight) {
      await this.nativeSyncInFlight;
    }

    const uidAfterWait = this.firebaseAuth.currentUser?.uid;
    if (!uidAfterWait) {
      return;
    }
    if (
      !force &&
      uidAfterWait === this.lastSyncedUid &&
      this.lastSuccessfulNativeSyncAt > 0 &&
      Date.now() - this.lastSuccessfulNativeSyncAt < NATIVE_SYNC_MIN_INTERVAL_MS
    ) {
      return;
    }

    this.nativeSyncInFlight = this.runSyncNativeSession();
    try {
      await this.nativeSyncInFlight;
    } finally {
      this.nativeSyncInFlight = null;
    }
  }

  /**
   * Mint a custom token (callable) and sign in the native Firebase Auth SDK.
   * Invoked from the authState subscription, app resume, or any caller that needs an explicit refresh.
   * Does not throw; logs failures so login is not blocked.
   */
  async syncNativeSessionAfterWebAuth(): Promise<void> {
    await this.maybeSyncNativeSession({ force: false });
  }

  /**
   * Call before native Storage uploads so FirebaseAuth.currentUser exists in the plugin.
   * Respects the same throttle as authState; use { force: true } after a known native-auth upload error.
   */
  async ensureNativeSessionForUpload(options?: { force?: boolean }): Promise<void> {
    await this.maybeSyncNativeSession(options);
  }

  async signOutNative(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    this.lastSuccessfulNativeSyncAt = 0;
    this.lastSyncedUid = null;
    try {
      await FirebaseUpload.signOutFirebaseAuth();
    } catch (e) {
      console.error('[NativeFirebaseAuthBridge] signOutNative failed', e);
    }
  }
}
